"use client";

import { ColumnDef } from "@tanstack/react-table";
import { capitalize, upperCase } from "lodash";
import { CircleArrowDown, CircleArrowUp } from "lucide-react";
import moment from "moment";
import Image from "next/image";

import EditPaperTransaction from "@/components/AddTransaction/editTransaction";
import ExchangeImage from "@/components/global/exchange-swticher/exchangeIcon";
import { Badge } from "@/components/ui/badge";
import { DataTableColumnHeader } from "@/components/ui/data-table-col-header";
import {
  formatNumber,
  percentageFormat,
  textColorBasedOnNumber,
} from "@/lib/utils";
import * as m from "@/paraglide/messages";

import { TransactionsTypes } from "./types";
import { orderOriginEnums, orderTypesEnums } from "../types";

export const columns: ColumnDef<TransactionsTypes>[] = [
  {
    accessorKey: "order_data",
    meta: {
      title: m.type(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.type()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2 items-center text-sm font-semibold">
            {row.original.order_data.side === "BUY" ? (
              <CircleArrowUp className="rotate-45 text-success dark:text-green-600" />
            ) : (
              <CircleArrowDown className="-rotate-45 text-destructive dark:text-red-600" />
            )}
            {row.original.order_data.side === "BUY"
              ? m.buy()
              : m.sell() ?? m.sell()}
          </div>
          <p className="text-left text-xs  text-muted-foreground">
            {
              orderOriginEnums[
                row.original.order_origin as keyof typeof orderOriginEnums
              ]
            }
          </p>
        </div>
      );
    },
    size: 160,
  },
  {
    accessorKey: "order_status",
    meta: {
      title: m.status(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.status()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-bold text-sm flex flex-col gap-1">
          {capitalize(upperCase(row.original.order_status))}
          <p className="text-left text-xs font-medium text-muted-foreground">
            {row.original.type === "ENTRY" &&
              capitalize(row.original.order_data.entry_type) + "-"}
            {row.original.type === "T_SL"
              ? row.original.order_data.trailing_delta
                ? m.percentage()
                : m.breakeven()
              : orderTypesEnums[
                  row.original.type as keyof typeof orderTypesEnums
                ]}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "settled_at",
    meta: {
      title: m.date(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.date()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium">
          {moment(row.original.settled_at ?? row.original.created_at).format(
            "MMMM DD,YYYY",
          )}
          <p className="text-sm">
            {moment(row.original.settled_at ?? row.original.created_at).format(
              "HH:mm:ss",
            )}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "order_symbol",
    meta: {
      title: m.asset(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.asset()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="font-medium ">
          <div className="flex gap-1 items-center justify-start w-full">
            <Image
              alt={row.original.asset_name}
              src={row.original.asset_logo}
              width={24}
              height={24}
              className="rounded-full md:mr-4 mr-1"
              key={row.original.id}
            />
            <div className="flex flex-col w-full items-start justify-start">
              <p className="font-medium text-left md:text-sm text-xs">
                {formatNumber(row.original.executed_amount)}{" "}
                {row.original?.asset_name.toUpperCase()}
                <span className=" text-grey-1">
                  {"/"}
                  {row.original.base_name.toUpperCase()}
                </span>
              </p>
              <p className="text-xs font-semibold text-muted-foreground text-left">
                {formatNumber(
                  row.original.total_price,
                  true,
                  false,
                  1,
                  row.original.base_name.toUpperCase(),
                )}{" "}
                (1 {row.original.asset_name.toUpperCase()} ={" "}
                {formatNumber(
                  row.original.executed_price ?? 0,
                  true,
                  false,
                  1,
                  row.original.base_name.toUpperCase(),
                )}
                )
              </p>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "total_fees_applied",
    meta: {
      title: m.order_fee(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.order_fee()} />;
    },
    cell: ({ row }) => {
      return (
        <div className="text-right ">
          {formatNumber(
            parseFloat(
              row.original?.total_fees_applied?.cost.toString() ?? "0",
            ),
            true,
            false,
            1,
            "",
          )}
          {" " +
            (row?.original?.total_fees_applied?.currency
              ? row?.original?.total_fees_applied?.currency
              : "USDT")}
        </div>
      );
    },
  },
  {
    accessorKey: "value",
    meta: {
      title: m.current_value(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title={m.current_value()} />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="ps-8 text-right">
          {formatNumber(
            (row.original.current_price ?? 0) * row.original.executed_amount,
            true,
            false,
            1,
            "",
          ) +
            " " +
            (row.original.base_name.toUpperCase()
              ? row.original.base_name.toUpperCase()
              : "USDT")}
        </div>
      );
    },
  },
  {
    accessorKey: "total_price",
    meta: {
      title: m.pnl(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.pnl()} />;
    },
    cell: ({ row }) => {
      const price = row.original.current_price;
      const execPrice = row.original.executed_price;
      const avgBuy = row.original.avg_buy_price;
      const side = row.original.order_data.side;
      const final =
        side?.toLowerCase() === "sell"
          ? ((execPrice - avgBuy) / avgBuy) * 100
          : ((price - execPrice) / execPrice) * 100;

      return (
        <div className="text-right">
          <Badge
            variant={
              final > 0 && final !== Infinity
                ? "success"
                : final === 0 || isNaN(final) || final === Infinity
                ? "default"
                : "destructive"
            }
          >
            {isNaN(final) || final === Infinity
              ? 0
              : percentageFormat(final, true)}
            %
          </Badge>
        </div>
      );
    },
  },
  {
    accessorKey: "order_provider",
    meta: {
      title: m.exchange(),
      shouldStopHideFN: true,
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          title={m.exchanges()}
          enableSorting={false}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="ps-10 text-center ">
          {row.original.order_data.notes === "paper" ? (
            <Image
              key={row.index}
              src="/paper.svg"
              alt="Paper Account Icon"
              width={20}
              height={20}
            />
          ) : (
            <ExchangeImage
              key={row.index}
              providerId={row.original.order_provider}
              width={17}
            ></ExchangeImage>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "order_origin",
    meta: {
      title: m.action(),
      shouldStopHideFN: true,
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          title={m.action()}
          enableSorting={false}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="ps-8">
          <EditPaperTransaction selectedItem={row.original} />
        </div>
      );
    },
  },
];

export const mobileCols: ColumnDef<TransactionsTypes>[] = [
  {
    accessorKey: "order_data.side",
    meta: {
      title: m.asset(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.asset()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex gap-1 items-center">
          <Image
            alt={row.original.asset_name}
            src={row.original.asset_logo ?? "/defaultIcon.svg"}
            width={20}
            height={20}
            className="rounded-full md:mr-4 mr-1"
            key={row.original.id}
          />
          <div className="leading-[1.4]">
            <div className="flex items-center text-sm font-bold">
              <span
                className={textColorBasedOnNumber(
                  row.original.order_data.side === "BUY" ? 1 : -1,
                )}
              >
                {row.original.order_data.side === "BUY"
                  ? m.buy().toUpperCase()
                  : (m.sell() ?? m.sell()).toUpperCase()}
              </span>
              <p className="ms-0.5">
                {row.original?.asset_name.toUpperCase()}
                <span className=" text-grey-1">
                  {"/"}
                  {row.original.base_name.toUpperCase()}
                </span>
              </p>
            </div>
            <p className="text-left text-xs text-muted-foreground">
              {row.original.order_origin === "manual_order"
                ? row.original.type === "T_SL"
                  ? row.original.order_data.trailing_delta
                    ? m.percentage()
                    : m.breakeven()
                  : orderTypesEnums[
                      row.original.type as keyof typeof orderTypesEnums
                    ]
                : orderOriginEnums[
                    row.original.order_origin as keyof typeof orderOriginEnums
                  ]}
            </p>
            <p className="text-xs text-muted-foreground">
              {moment(
                row.original.settled_at ?? row.original.created_at,
              ).format("MMMM DD,YYYY")}
            </p>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "executed_amount",
    meta: {
      title: m.order(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-end"
          title={m.order()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="leading-[1.4] text-right">
          <div className="flex flex-row items-center justify-end text-sm font-bold">
            <p
              className={textColorBasedOnNumber(
                row.original.order_data.side === "BUY" ? 1 : -1,
              )}
            >
              {formatNumber(row.original.executed_amount)}{" "}
            </p>
            <p className="ms-0.5 text-right">
              {row.original?.asset_name.toUpperCase()}
            </p>
          </div>

          <p className="text-xs text-muted-foreground">
            {formatNumber(
              row.original.total_price,
              true,
              false,
              1,
              row.original.base_name.toUpperCase(),
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            @ {formatNumber(row.original.executed_price ?? 0, true)}
          </p>
        </div>
      );
    },
  },
];

export const hiddenColumns = (isPapaerAccount: boolean) => ({
  order_provider: !isPapaerAccount,
  order_origin: isPapaerAccount,
});
